import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import Drawer from '../../../components/ui/Drawer';
import TabBar from '../../../components/TabBar';
import { useDrawerResize } from '../../../hooks/useDrawerResize';
import NotesPanel from '../../../components/common/NotesPanel';
import { useResolvedAccessLevel } from '../../../components/PermissionsContainer';
import safetySystemService from '../../../services/safetySystem.service';
import type { TicketDetail, TicketDupe, TicketLookups, TicketSaveBody, TicketSaveResponse } from '../../../types/safetySystem';
import SafetyDocuments from './SafetyDocuments';
import TicketInfoTab from './TicketInfoTab';
import { EquipmentTab, TICKET_HISTORY_LABELS, ViolationsTab } from './TicketRowsTabs';
import { DiscardFooter, ResizeHandle, SafetyHistoryTab, errorText } from './safetyDrawerShared';
import { diffChanges, isDirty } from './unitForm';
import { str } from './unitFields';
import {
  ChildRow, EQUIPMENT_FIELDS, VIOLATION_FIELDS, childRowsForSave, childrenDirty, isClosed, newEquipmentRow, newTicketDefaults,
  newViolationRow, pickTicketFields, totalTicketWeight, withKeys,
} from './ticketForm';

/**
 * Ticket drawer — port of the V1 Safety ticket page. Tabs: Ticket Info, Violations,
 * Equipment, Documents, Notes, History. `ticketrecno === null` is a brand-new ticket:
 * the Documents / Notes / History tabs stay disabled until the first save creates it,
 * after which the drawer stays open on the created recno.
 *
 * Save posts `{ ticket: <changed keys> (+ closed), violations, equipment, bypass_dupe }`
 * (see ticketForm.childRowsForSave). A 409 renders the duplicates with "Save anyway";
 * a 422 string shows inline; `warnings` on a 200 render as a notice and keep the drawer
 * open so the user can act on them (they never block closing).
 */

export interface TicketDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  ticketrecno: number | null;
  onSaved: () => void;
}

type TabId = 'info' | 'violations' | 'equipment' | 'documents' | 'notes' | 'history';
const SAVE_TABS: TabId[] = ['info', 'violations', 'equipment'];

/** Officers / equipment owners from the last load, the fallback while a New Ticket drawer fetches GET /tickets/lookups. */
let lookupsCache: TicketLookups | null = null;
const EMPTY_LOOKUPS: TicketLookups = { officers: [], equipowners: ['SELF'] };

const newDetail = (lookups: TicketLookups): TicketDetail => ({
  ticket: { ...newTicketDefaults(), tdrivername: '' },
  violations: [],
  equipment: [],
  lookups,
});

export default function TicketDrawer({ isOpen, onClose, ticketrecno, onSaved }: TicketDrawerProps) {
  const canEdit = useResolvedAccessLevel('safety-system-unit-edit', true) >= 2;
  const { width, onHandleMouseDown, onHandleDoubleClick } = useDrawerResize({
    isOpen, storageKey: 'safetyTicketDrawerWidth', minWidth: 480, defaultWidth: 1200,
  });
  const { accounts } = useMsal();
  const currentUser = accounts[0]?.username || accounts[0]?.name || '';

  // The recno the drawer is showing: the prop, or the recno a create just produced.
  const [recno, setRecno] = useState<number | null>(ticketrecno);
  useEffect(() => { setRecno(ticketrecno); }, [ticketrecno, isOpen]);

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState<TabId>('info');
  const [noteCount, setNoteCount] = useState(0);

  // Editable copies + the pristine originals (in `detail`) they are diffed against on Save.
  const [ticket, setTicket] = useState<Record<string, any>>({});
  const [closed, setClosed] = useState(false);
  const [driverText, setDriverText] = useState('');
  const [violations, setViolations] = useState<ChildRow[]>([]);
  const [equipment, setEquipment] = useState<ChildRow[]>([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [dupes, setDupes] = useState<TicketDupe[] | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen && contentRef.current) setPortalContainer(contentRef.current.closest('dialog') || null);
  }, [isOpen, detail]);

  const resetForm = useCallback((d: TicketDetail | null) => {
    setTicket(d ? pickTicketFields(d.ticket) : {});
    setClosed(d ? isClosed(d.ticket.closedatetime) : false);
    setDriverText(d ? str(d.ticket.tdrivername) : '');
    setViolations(d ? withKeys(d.violations ?? []) : []);
    setEquipment(d ? withKeys(d.equipment ?? []) : []);
    setSaveError('');
    setDupes(null);
    setConfirmDiscard(false);
  }, []);

  const load = useCallback((id: number, signal?: AbortSignal) => {
    setLoading(true);
    setLoadError('');
    return safetySystemService.getTicket(id, signal)
      .then((d) => {
        if (signal?.aborted) return;
        lookupsCache = d.lookups ?? lookupsCache;
        setDetail(d);
        resetForm(d);
      })
      .catch((e) => {
        if (signal?.aborted) return;
        setDetail(null);
        setLoadError(e?.response?.data?.detail || 'Unable to load this ticket.');
      })
      .finally(() => { if (!signal?.aborted) setLoading(false); });
  }, [resetForm]);

  // The tab / notices reset when the drawer opens or is pointed at another ticket by its
  // owner -- NOT when a create switches `recno` to the new record, so the user stays on
  // the tab they created from and keeps the create's warnings.
  useEffect(() => {
    setActiveTab('info');
    setNoteCount(0);
    setWarnings([]);
  }, [isOpen, ticketrecno]);

  // Load on open / ticket change. A null recno is a new ticket: blank form + the pick
  // lists from GET /tickets/lookups (the cache from the last load while that is in flight).
  useEffect(() => {
    if (!isOpen) {
      setDetail(null);
      resetForm(null);
      return;
    }
    if (recno === null) {
      const d = newDetail(lookupsCache ?? EMPTY_LOOKUPS);
      setDetail(d);
      resetForm(d);
      setLoading(false);
      setLoadError('');
      const controller = new AbortController();
      safetySystemService.ticketLookups(controller.signal)
        .then((lk) => {
          if (controller.signal.aborted || !lk) return;
          lookupsCache = lk;
          setDetail((prev) => (prev && prev.ticket.recno === undefined ? { ...prev, lookups: lk } : prev));
        })
        .catch(() => { /* the cached / empty lists stand; the drawer still works */ });
      return () => controller.abort();
    }
    const controller = new AbortController();
    load(recno, controller.signal);
    return () => controller.abort();
  }, [isOpen, recno, load, resetForm]);

  const isNew = recno === null;
  const baseTicket = useMemo(() => (detail ? pickTicketFields(detail.ticket) : {}), [detail]);
  const baseClosed = detail ? isClosed(detail.ticket.closedatetime) : false;
  const totalWeight = totalTicketWeight(violations);
  const equipmentUnits = equipment.filter((r) => str(r.deleted).toUpperCase() !== 'Y').map((r) => str(r.ticketequipunit));

  const dirty = useMemo(() => {
    if (!detail) return false;
    return isDirty(baseTicket, ticket) || closed !== baseClosed
      || childrenDirty(detail.violations ?? [], violations, VIOLATION_FIELDS)
      || childrenDirty(detail.equipment ?? [], equipment, EQUIPMENT_FIELDS);
  }, [detail, baseTicket, ticket, closed, baseClosed, violations, equipment]);

  const clearErrors = () => { setSaveError(''); setDupes(null); };
  const onTicketChange = (key: string, value: unknown) => { setTicket((p) => ({ ...p, [key]: value })); clearErrors(); };
  const onDriverPick = (driverrecno: number, label: string) => { onTicketChange('driverrecno', driverrecno); setDriverText(label); };

  const buildBody = (bypass: boolean): TicketSaveBody => {
    const body: TicketSaveBody = {
      ticket: diffChanges(baseTicket, ticket),
      violations: childRowsForSave(detail?.violations ?? [], violations, VIOLATION_FIELDS),
      equipment: childRowsForSave(detail?.equipment ?? [], equipment, EQUIPMENT_FIELDS),
      bypass_dupe: bypass,
    };
    if (closed !== baseClosed) body.ticket.closed = closed;
    return body;
  };

  const handleSave = async (bypass = false) => {
    if (!detail || !SAVE_TABS.includes(activeTab)) return;
    if (!isNew && !dirty) { onClose(); return; }
    const body = buildBody(bypass);
    setSaving(true);
    clearErrors();
    setWarnings([]);
    try {
      let res: TicketSaveResponse;
      if (isNew) {
        res = await safetySystemService.createTicket(body);
      } else {
        res = await safetySystemService.saveTicket(recno as number, body);
      }
      const warn = res?.warnings ?? [];
      setWarnings(warn);
      onSaved();
      if (isNew) {
        // Stay open on the created ticket so Documents / Notes / History become available.
        setRecno(res.recno);
      } else if (warn.length) {
        await load(recno as number);
      } else {
        onClose();
      }
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && Array.isArray(d.dupes)) setDupes(d.dupes as TicketDupe[]);
      setSaveError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const requestClose = (): boolean => {
    if (dirty) { setConfirmDiscard(true); return false; }
    return true;
  };
  const handleCancel = () => { if (requestClose()) onClose(); };
  const handleDiscard = () => { resetForm(detail); onClose(); };

  const ticketnum = str(ticket.ticketnum ?? detail?.ticket?.ticketnum);
  const county = str(ticket.ticketcounty ?? detail?.ticket?.ticketcounty);
  const title = detail ? (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-lg font-semibold">
        {isNew && !ticketnum ? 'New Ticket' : `Ticket: ${ticketnum}${county ? ` - ${county}` : ''}`}
      </span>
      {driverText && <span className="text-sm text-muted">{`Driver: ${driverText}`}</span>}
      {!isNew && (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${baseClosed ? 'bg-gray-200 text-heading dark:bg-gray-700' : 'bg-accent text-accent-fg'}`}>
          {baseClosed ? 'CLOSED' : 'OPEN'}
        </span>
      )}
    </div>
  ) : (loading ? 'Loading ticket…' : 'Ticket');

  const tabs = [
    { id: 'info', label: 'Ticket Info', current: activeTab === 'info' },
    { id: 'violations', label: 'Violations', current: activeTab === 'violations', ...(violations.length ? { badge: totalWeight } : {}) },
    { id: 'equipment', label: 'Equipment', current: activeTab === 'equipment' },
    { id: 'documents', label: 'Documents', current: activeTab === 'documents', disabled: isNew },
    { id: 'notes', label: 'Notes', current: activeTab === 'notes', disabled: isNew, ...(noteCount > 0 ? { badge: noteCount } : {}) },
    { id: 'history', label: 'History', current: activeTab === 'history', disabled: isNew },
  ];

  const footer = confirmDiscard ? (
    <DiscardFooter onDiscard={handleDiscard} onKeep={() => setConfirmDiscard(false)} />
  ) : (
    <div className="flex items-center justify-end gap-2">
      <button type="button" onClick={handleCancel} className="rounded-md border border-rule px-3 py-1.5 text-sm hover:bg-highlight">Cancel</button>
      {canEdit && detail && SAVE_TABS.includes(activeTab) && (
        <button
          type="button"
          onClick={() => handleSave(false)}
          disabled={saving || loading}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-medium text-accent-fg hover:bg-accent-hover disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 dark:disabled:bg-gray-700 dark:disabled:text-gray-400"
        >
          {saving ? 'Saving…' : isNew ? 'Create Ticket' : 'Save'}
        </button>
      )}
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      onBeforeClose={requestClose}
      title={title}
      maxWidth="full"
      width={`${width}px`}
      contentClassName="relative flex-1 overflow-y-auto px-4 py-4 sm:px-6"
      footer={footer}
    >
      <ResizeHandle width={width} onHandleMouseDown={onHandleMouseDown} onHandleDoubleClick={onHandleDoubleClick} />
      <div ref={contentRef}>
        <TabBar tabs={tabs} onTabChange={(id) => setActiveTab(id as TabId)} compact noBorder className="mb-4" />

        {loadError && <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">{loadError}</div>}
        {saveError && (
          <div role="alert" className="mb-3 rounded-md border border-danger bg-danger px-3 py-2 text-sm text-danger-fg">
            <div>{saveError}</div>
            {dupes && dupes.length > 0 && (
              <div className="mt-2 flex flex-wrap items-start justify-between gap-2">
                <ul className="list-disc pl-5">
                  {dupes.map((d) => <li key={d.recno}>{`#${d.recno} ${d.ticketnum}${d.tdrivername ? ` — ${d.tdrivername}` : ''}`}</li>)}
                </ul>
                {canEdit && (
                  <button type="button" onClick={() => handleSave(true)} disabled={saving}
                    className="rounded-md border border-rule bg-white px-3 py-1 text-sm font-medium text-heading hover:bg-highlight disabled:cursor-not-allowed dark:bg-gray-900">
                    Save anyway
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <div role="status" className="mb-3 rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-600 dark:bg-amber-950 dark:text-amber-100">
            <div className="font-medium">Saved with warnings</div>
            <ul className="list-disc pl-5">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </div>
        )}

        {loading && <div className="py-8 text-center text-sm text-muted">Loading ticket…</div>}

        {!loading && detail && activeTab === 'info' && (
          <TicketInfoTab
            detail={detail}
            ticket={ticket}
            onChange={onTicketChange}
            driverText={driverText}
            onDriverText={setDriverText}
            onDriverPick={onDriverPick}
            closed={closed}
            onClosedChange={(v) => { setClosed(v); clearErrors(); }}
            totalWeight={totalWeight}
            canEdit={canEdit}
            portalContainer={portalContainer}
          />
        )}
        {!loading && detail && activeTab === 'violations' && (
          <ViolationsTab
            rows={violations}
            onRowsChange={(rows) => { setViolations(rows); clearErrors(); }}
            equipmentUnits={equipmentUnits}
            canEdit={canEdit}
            onAdd={() => setViolations((p) => [...p, newViolationRow()])}
          />
        )}
        {!loading && detail && activeTab === 'equipment' && (
          <EquipmentTab
            rows={equipment}
            onRowsChange={(rows) => { setEquipment(rows); clearErrors(); }}
            owners={detail.lookups?.equipowners ?? ['SELF']}
            canEdit={canEdit}
            onAdd={() => setEquipment((p) => [...p, newEquipmentRow()])}
          />
        )}
        {!loading && detail && activeTab === 'documents' && recno !== null && (
          <SafetyDocuments tablename="ticket" tablerecno={recno} isReadOnly={!canEdit} />
        )}
        {!loading && detail && activeTab === 'notes' && recno !== null && (
          <NotesPanel
            fetchNotes={async () => ({ notes: (await safetySystemService.listNotes('ticket', recno)).data ?? [] })}
            addNote={canEdit ? async (text) => { await safetySystemService.addNote('ticket', recno, text); } : null}
            onNoteCountChange={setNoteCount}
            isReadOnly={!canEdit}
            currentUser={currentUser}
          />
        )}
        {!loading && detail && activeTab === 'history' && recno !== null && (
          <SafetyHistoryTab recno={recno} fetch={safetySystemService.getTicketHistory} labels={TICKET_HISTORY_LABELS} emptyText="No history for this ticket." />
        )}
      </div>
    </Drawer>
  );
}
